import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { Eye, ExternalLink } from "lucide-react";

export default function CustomPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const previewMode = searchParams.get("preview") === "1";
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    setLoading(true);
    setPreviewError("");
    const endpoint = previewMode ? `/admin/pages/preview/${slug}` : `/pages/${slug}`;
    api.get(endpoint)
      .then((r) => setPage(r.data))
      .catch((e) => {
        setPage(null);
        if (previewMode) {
          const detail = e?.response?.data?.detail;
          if (e?.response?.status === 401 || e?.response?.status === 403) {
            setPreviewError("Anda harus login sebagai admin untuk preview draft.");
          } else {
            setPreviewError(typeof detail === "string" ? detail : "Halaman tidak ditemukan.");
          }
        }
      })
      .finally(() => setLoading(false));
  }, [slug, previewMode]);

  if (loading) return <div className="container-sf py-40 text-center text-slate-500">Memuat halaman...</div>;

  if (!page) return (
    <div className="container-sf py-40 text-center">
      <h1 className="text-3xl font-bold text-slate-900">Halaman tidak ditemukan</h1>
      {previewError && <p className="mt-3 text-sm text-slate-600">{previewError}</p>}
      <Link to="/" className="btn-primary mt-6">Kembali ke Beranda</Link>
      {previewError && user && (
        <Link to="/admin/pages" className="ml-3 btn-ghost">Kembali ke Admin</Link>
      )}
    </div>
  );

  const isDraft = !page.published;

  return (
    <div data-testid={`page-custom-${slug}`}>
      <SeoHead
        title={page.title}
        description={page.meta_description || page.subtitle}
        image={page.hero_image}
      />

      {/* Preview banner */}
      {previewMode && user && (
        <div className="sticky top-0 z-40 bg-[var(--brand-orange)] text-white" data-testid="preview-banner">
          <div className="container-sf py-2.5 flex items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-2 font-semibold">
              <Eye className="h-4 w-4" />
              <span>Mode Preview {isDraft ? "— Halaman ini DRAFT (belum publish)" : "— Halaman ini sudah LIVE"}</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/pages" className="hover:underline">Kembali ke Admin</Link>
              {!isDraft && (
                <a href={`/p/${slug}`} className="inline-flex items-center gap-1 hover:underline">
                  Lihat versi live <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <PageHeader
        eyebrow={isDraft && previewMode ? "Draft" : "Halaman"}
        title={page.title}
        description={page.subtitle}
        image={page.hero_image}
      />

      <section className="section-y bg-white">
        <div className="container-sm">
          <div
            className="prose prose-slate max-w-none prose-headings:font-bold prose-a:text-[var(--brand-blue)] prose-img:rounded-2xl"
            dangerouslySetInnerHTML={{ __html: page.content || "<p><em>(Halaman ini belum memiliki konten. Silakan edit di admin panel.)</em></p>" }}
          />
        </div>
      </section>
    </div>
  );
}
